/**
 * InsiBirthPlaceAutofill — auto-fills the birth-place fields from the INSEE code
 * that INSi returns on a confirmed identity (`tiq.lieuNaissance`).
 *
 * Rendered inside the patient form (create INSi mode, and the edit re-search
 * "matched" state). When an INSEE code is present it reverse-looks-up the commune
 * (geo.api.gouv.fr) and fills city / department / country, so the PS never has to
 * search the birth place manually for an INSi-identified patient.
 *
 * Renders nothing. The INSi `lieuNaissance` code is always stored in the
 * dedicated `birthPlaceInseeCode` field; the human-readable `birthPlace`
 * (city / postal code / department / country) is filled only when the code
 * resolves to a French commune. Foreign COG-Pays codes (99xxx) and unknown
 * codes keep the code alone, with an empty `birthPlace`.
 */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-final-form';

import { lookupCommuneByCode } from 'services/geo/communes';

export default function InsiBirthPlaceAutofill({ inseeCode }) {
  const form = useForm();

  useEffect(() => {
    let cancelled = false;

    // The matched INSi identity is authoritative. If it carries NO birth-place
    // code (some foreign identities return an empty lieuNaissance), clear any
    // prior value rather than leaving the previously-selected patient's place
    // lingering on the form.
    if (!inseeCode) {
      form.batch(() => {
        form.change('birthPlaceInseeCode', undefined);
        form.change('birthPlace', {});
      });
      return undefined;
    }

    lookupCommuneByCode(inseeCode).then((commune) => {
      if (cancelled) return;
      form.batch(() => {
        // `lieuNaissance` is the authoritative birth-place identifier — a French
        // commune INSEE code, or a foreign 99xxx COG-Pays code. Always store it
        // in its dedicated field (never in birthPlace.zipcode, which is a postal
        // code, not an INSEE/COG code).
        form.change('birthPlaceInseeCode', inseeCode);
        // Enrich the human-readable birthPlace only for codes geo can resolve
        // (French communes): real postal code in zipcode, plus city/department.
        // Foreign / unknown codes don't resolve → clear birthPlace, keep only the
        // code (dropping any stale prior place, which has no UI to clear it).
        form.change(
          'birthPlace',
          commune
            ? {
                city: commune.nom,
                zipcode: commune.codesPostaux?.[0] || '',
                state: commune.departementNom,
                country: 'France',
              }
            : {}
        );
      });
    });

    return () => {
      cancelled = true;
    };
  }, [inseeCode, form]);

  return null;
}

InsiBirthPlaceAutofill.propTypes = {
  /** INSEE code from the confirmed INSi identity (`tiq.lieuNaissance`). */
  inseeCode: PropTypes.string,
};

InsiBirthPlaceAutofill.defaultProps = {
  inseeCode: undefined,
};
